'use client';

import { createContext, useContext, type ReactNode } from 'react';
import { useMobileViewport } from '../../lib/useMobile';

/** How many overlays deep the tree currently is. 0 means nothing is open yet, so the next overlay is
 *  the FIRST click out of a section. */
const OverlayDepthContext = createContext(0);

function useOverlayDepth(): number {
  return useContext(OverlayDepthContext);
}

/** Wraps an overlay's own children so anything opened from inside it counts as one level deeper.
 *  A React portal keeps its owner's context, so this stays accurate even though every overlay renders
 *  into document.body rather than where it was written.
 *
 *  `standsInForPage` is the one exception, and it is not a nesting exemption: it says the overlay IS a
 *  page — an intercepted route rendered over whatever linked to it, as `/settings` is — rather than a
 *  step taken from one. Everything inside such a surface has to resolve exactly as it does on the
 *  canonical page, so the first editor opened from it is still a right-hand drawer instead of a centered
 *  window. Counting it as depth 1 turned every nested Settings editor into a centered dialog at once. */
export function OverlayDepthProvider({ children, standsInForPage = false }: { children: ReactNode; standsInForPage?: boolean }) {
  const depth = useOverlayDepth();
  return <OverlayDepthContext.Provider value={standsInForPage ? 0 : depth + 1}>{children}</OverlayDepthContext.Provider>;
}

/** How much room the window has. `phone` is the same breakpoint the stylesheets use — see
 *  `lib/breakpoints.ts`, which both this and every CSS mirror read from. */
export type OverlayViewport = 'phone' | 'roomy';

export type OverlayPresentation = 'drawer' | 'center' | 'sheet' | 'fullscreen';

/** The house rule, in one place. It is a function of BOTH how deep the overlay is and how much room
 *  the window has, because either one alone gives the wrong answer:
 *
 *    - With room, the first click inside a section opens a right-hand drawer and a centered window is
 *      only ever a step taken FROM an already-open drawer.
 *    - On a phone neither of those exists. A drawer that leaves a 48px strip of backdrop is not a
 *      layer, it is a broken layout, and a centered window inside a 390px viewport is a desktop dialog
 *      that happens to be small. Every automatic overlay therefore takes the full screen — there is no
 *      room to show two layers at once, and a partial-height sheet hides half the surface just opened.
 *
 *  What is opened is a second axis, stated here because this is where the rule lives. A record, including
 *  an entity detail with tabs (a Project, a User), is the drawer this function answers for. A surface whose
 *  sections are settings of their own (Settings, the Users account defaults and presets) is a WINDOW: a
 *  `PageOverlay` over a `SectionDeck`, which is `standsInForPage` and therefore does not count as depth.
 *  On a phone both are fullscreen. `docs/modules/web/components.md`, "Detail window and drawer", is the
 *  long form.
 *
 *  Resolved at render time so a control cannot pick the wrong one and no call site has to remember the
 *  rule. `resolveOverlayPresentation(depth)` still answers the desktop question on its own, which is
 *  what the geometry tests and any non-React caller need. */
export function resolveOverlayPresentation(
  depth: number,
  viewport: OverlayViewport = 'roomy',
): OverlayPresentation {
  if (viewport === 'phone') return 'fullscreen';
  return depth === 0 ? 'drawer' : 'center';
}

/** The rule above, bound to the live viewport. "Not measured yet" reads as roomy: that is the safe
 *  first paint everywhere, and `useMobileViewport` settles within the same commit as the overlay's own
 *  mount effect, so a phone never paints the desktop presentation. */
export function useOverlayPresentation(): OverlayPresentation {
  const depth = useOverlayDepth();
  const phone = useMobileViewport() === true;
  return resolveOverlayPresentation(depth, phone ? 'phone' : 'roomy');
}
